import React, { useState } from "react";
import { 
  FileText, 
  Edit3, 
  File, 
  MessageSquare, 
  Globe, 
  Calendar,
  Plus,
  Building2,
} from 'lucide-react';
import LawFirmModal from './LawFirmModal';

const FeatureCard = ({ title, description, icon: Icon, color, onClick }) => (
  <div 
    className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-6 hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)] transition-all cursor-pointer"
    onClick={onClick}
  >
    <div className="flex items-center gap-4 mb-3">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
    </div>
    <p className="text-sm text-gray-700/90">{description}</p>
  </div>
);

const ActionCard = ({ title, description, icon: Icon, color, onClick, buttonText }) => (
  <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-6">
    <div className="flex items-center gap-4 mb-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <p className="text-sm text-gray-700/90">{description}</p>
      </div>
    </div>
    <button 
      onClick={onClick}
      className="w-full bg-gradient-to-r from-gray-700 to-gray-500 hover:from-gray-800 hover:to-gray-600 text-white py-2 px-4 rounded-xl transition-all shadow-sm"
    >
      {buttonText}
    </button>
  </div>
);

const IndividualLawyerDashboard = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('join'); // 'join' or 'create'

  const handleJoinFirm = () => {
    setModalMode('join');
    setModalOpen(true);
  };

  const handleCreateFirm = () => {
    setModalMode('create');
    setModalOpen(true);
  };

  const handleJoin = (inviteCode) => {
    // TODO: Implement API call to join law firm
    console.log('Joining firm with code:', inviteCode);
    // After successful join, update user type to 'lawfirm'
  };

  const handleCreate = (firmData) => {
    // TODO: Implement API call to create law firm
    console.log('Creating firm:', firmData);
    // After successful creation, update user type to 'lawfirm'
  };

  return (
    <div className="p-6 space-y-8">
      {/* Welcome Section */}
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-[linear-gradient(90deg,#1f2937_0%,#4b5563_45%,#9ca3af_100%)] mb-4">
          Welcome to Your Legal Practice
        </h1>
        <p className="text-gray-700/80 max-w-2xl mx-auto">
          Get started with our powerful legal tools. Use them individually or join a law firm to collaborate with your team.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ActionCard
          title="Join a Law Firm"
          description="Enter an invite code to join an existing law firm and collaborate with your team"
          icon={Building2}
          color="bg-blue-600"
          onClick={handleJoinFirm}
          buttonText="Enter Invite Code"
        />
        <ActionCard
          title="Create a Law Firm"
          description="Start your own law firm and invite other lawyers to join your practice"
          icon={Plus}
          color="bg-green-600"
          onClick={handleCreateFirm}
          buttonText="Create Firm"
        />
      </div>

      {/* Individual Features */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Your Legal Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            title="Document Translation"
            description="Translate legal documents between multiple languages with AI accuracy"
            icon={Globe}
            color="bg-[#0d3b66]"
            onClick={() => window.location.href = '/dashboard/translate'}
          />
          <FeatureCard
            title="Draft Management"
            description="Create, edit, and manage your legal document drafts"
            icon={Edit3}
            color="bg-[#059669]"
            onClick={() => window.location.href = '/dashboard/drafts'}
          />
          <FeatureCard
            title="Doc AI Assistant"
            description="AI-powered legal document analysis and insights"
            icon={File}
            color="bg-[#7c3aed]"
            onClick={() => window.location.href = '/dashboard/docai'}
          />
          <FeatureCard
            title="Query Bot"
            description="Get instant answers to legal questions and case research"
            icon={MessageSquare}
            color="bg-[#dc2626]"
            onClick={() => window.location.href = '/dashboard/query'}
          />
          <FeatureCard
            title="Document Library"
            description="Access and manage your personal document library"
            icon={FileText}
            color="bg-[#ea580c]"
            onClick={() => window.location.href = '/dashboard/available-documents'}
          />
          <FeatureCard
            title="Case Diary"
            description="Track your case progress, deadlines, and important events"
            icon={Calendar}
            color="bg-[#0891b2]"
            onClick={() => window.location.href = '/dashboard/adv-diary'}
          />
        </div>
      </div>

      {/* Getting Started Tips */}
      <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Getting Started</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-gradient-to-r from-gray-700 to-gray-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-white text-sm font-semibold">1</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Upload Documents</h4>
              <p className="text-sm text-gray-700/90">Start by uploading your legal documents to the system</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-gradient-to-r from-gray-700 to-gray-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-white text-sm font-semibold">2</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Use AI Tools</h4>
              <p className="text-sm text-gray-700/90">Leverage AI for document analysis and translation</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-gradient-to-r from-gray-700 to-gray-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-white text-sm font-semibold">3</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Join or Create Firm</h4>
              <p className="text-sm text-gray-700/90">Collaborate with other lawyers by joining a firm</p>
            </div>
          </div>
        </div>
      </div>

      {/* Law Firm Modal */}
      <LawFirmModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        onJoin={handleJoin}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default IndividualLawyerDashboard;
